import { ArrowPathIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useOverlayScrollbar } from '../DataTable/useOverlayScrollbar';
import { OverlayScrollThumb } from './OverlayScrollArea';
import { PDFViewerToolbar } from './PDFViewerToolbar';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.mjs`;

interface PDFViewerProps {
    pdfUrl: string;
    apiBaseUrl?: string;
    showControls?: boolean;
    className?: string;
    getAuthToken?: () => string | null;
    /** Left toolbar slot for context affordances (file-name label, version dropdown). */
    leadingControls?: React.ReactNode;
    /** Streaming-proxy path (default v1 pdf-proxy). null = pdfUrl is directly fetchable
     * (signed GCS URL): no proxy, no Drive rewrite, no Bearer leaked to another origin. */
    proxyPath?: string | null;
    /** Keep the scroll offset when pdfUrl changes, for a document re-rendered in place. */
    keepScrollOnReload?: boolean;
}

const PDFViewer: React.FC<PDFViewerProps> = ({
    pdfUrl,
    apiBaseUrl = '',
    showControls = true,
    className,
    getAuthToken,
    leadingControls,
    proxyPath = '/contents/pdf-proxy',
    keepScrollOnReload = false
}) => {
    const [numPages, setNumPages] = useState<number | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [scale, setScale] = useState<number>(1);
    const [contentWidth, setContentWidth] = useState<number>(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isPrinting, setIsPrinting] = useState<boolean>(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
    const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
    const blobUrlRef = useRef<string | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const pendingScrollTopRef = useRef<number | null>(null);

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
        setCurrentPage(1);
    };

    React.useEffect(() => {
        // Only the newest run may publish: racing fetches (StrictMode dev) change
        // `file` identity mid-load and strand react-pdf on "Loading page…" forever.
        let cancelled = false;
        let createdUrl: string | null = null;

        const loadPdfBlob = async () => {
            try {
                // Direct mode: pdfUrl is already fetchable on its own (a signed
                // GCS URL). No proxy, no Drive rewrite, no Authorization header.
                const isDirect = proxyPath === null;

                let requestUrl = pdfUrl;
                let isGoogleDrive = false;
                const headers: Record<string, string> = {};

                if (!isDirect) {
                    const googleDriveMatch = pdfUrl.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
                    let actualPdfUrl = pdfUrl;

                    if (googleDriveMatch) {
                        isGoogleDrive = true;
                        const fileId = googleDriveMatch[1];
                        actualPdfUrl = `https://drive.google.com/uc?id=${fileId}&export=download`;
                    }

                    requestUrl = `${apiBaseUrl}${proxyPath}?url=${encodeURIComponent(actualPdfUrl)}`;

                    const token = getAuthToken?.() ?? null;
                    if (token) {
                        headers.Authorization = `Bearer ${token}`;
                    }
                }

                const response = await fetch(requestUrl, { headers });

                if (!response.ok) throw new Error('Failed to fetch PDF');

                const blob = await response.blob();

                if (blob.type.includes('text/html') || blob.type === 'text/plain') {
                    if (isDirect) {
                        // A signed URL answering HTML means the signature expired
                        // or was rejected — GCS returns an XML/HTML error body.
                        throw new Error('This document link has expired. Please reload the page.');
                    }
                    throw new Error(
                        isGoogleDrive
                            ? 'Google Drive file is not publicly accessible. Please set sharing to "Anyone with the link can view".'
                            : 'Received HTML instead of PDF. The file may require authentication.'
                    );
                }

                // Superseded before we got here: don't create a URL at all, so
                // there is nothing to leak and `file` never changes identity.
                if (cancelled) return;

                const blobUrl = URL.createObjectURL(blob);
                createdUrl = blobUrl;
                blobUrlRef.current = blobUrl;
                if (keepScrollOnReload && contentRef.current) {
                    pendingScrollTopRef.current = contentRef.current.scrollTop;
                }
                setPdfBlob(blob);
                setPdfBlobUrl(blobUrl);
            } catch (error) {
                if (cancelled) return;
                const errorMsg = error instanceof Error ? error.message : 'Failed to load PDF';
                setErrorMessage(errorMsg);
            }
        };

        loadPdfBlob();

        return () => {
            cancelled = true;
            // Revoke THIS run's URL only: blobUrlRef may already point at a newer
            // run's, and revoking that breaks the live document.
            if (createdUrl) {
                URL.revokeObjectURL(createdUrl);
                if (blobUrlRef.current === createdUrl) {
                    blobUrlRef.current = null;
                }
            }
        };
    }, [pdfUrl, apiBaseUrl, proxyPath, keepScrollOnReload]);

    const file = useMemo(() => pdfBlobUrl ?? null, [pdfBlobUrl]);

    const canGoPrev = currentPage > 1;
    const canGoNext = !!numPages && currentPage < numPages;
    const decreaseZoom = () => setScale((s) => Math.max(0.5, Math.round((s - 0.1) * 10) / 10));
    const increaseZoom = () => setScale((s) => Math.min(2, Math.round((s + 0.1) * 10) / 10));
    const resetZoom = () => setScale(1);
    const goToPage = (page: number) => {
        setCurrentPage(page);
        scrollToPage(page);
    };

    const handlePrint = async () => {
        if (!pdfBlob) return;
        setIsPrinting(true);
        try {
            const url = URL.createObjectURL(pdfBlob);
            const printWindow = window.open(url, '_blank');
            if (!printWindow) {
                URL.revokeObjectURL(url);
                throw new Error('Failed to open print window');
            }
            const checkReady = setInterval(() => {
                try {
                    if (printWindow.document.readyState === 'complete') {
                        clearInterval(checkReady);
                        setTimeout(() => {
                            printWindow.print();
                            printWindow.onafterprint = () => {
                                printWindow.close();
                                URL.revokeObjectURL(url);
                            };
                            setIsPrinting(false);
                        }, 500);
                    }
                } catch {
                    clearInterval(checkReady);
                    setIsPrinting(false);
                }
            }, 100);
            setTimeout(() => {
                clearInterval(checkReady);
                setIsPrinting(false);
            }, 30000);
        } catch {
            setErrorMessage('Failed to print PDF. Try opening in a new tab instead.');
            setIsPrinting(false);
        }
    };

    const handleDownload = () => {
        if (!pdfBlob) return;
        setIsDownloading(true);
        try {
            const url = URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `document-${Date.now()}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch {
            setErrorMessage('Failed to download PDF. Try opening in a new tab instead.');
        } finally {
            setIsDownloading(false);
        }
    };

    const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
    // The shared overlay scrollbar (same hook as DataTable/OverlayScrollArea):
    // native bar hidden, a thumb painted on top of the page column.
    const overlay = useOverlayScrollbar();
    const { setScrollElement } = overlay;

    // Stable callback ref: an inline one re-runs (null, node) on every render.
    const setContentElement = useCallback(
        (node: HTMLDivElement | null) => {
            contentRef.current = node;
            setScrollElement(node);
        },
        [setScrollElement]
    );

    // The new file unmounts the old pages, so the offset is reapplied as pages render back in.
    const restoreScroll = (isLastPage: boolean) => {
        const target = pendingScrollTopRef.current;
        const el = contentRef.current;
        if (target === null || !el) return;
        el.scrollTop = target;
        if (isLastPage || el.scrollTop >= target) pendingScrollTopRef.current = null;
    };

    const scrollToPage = (pageNumber: number) => {
        const target = pageRefs.current[pageNumber - 1];
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    React.useEffect(() => {
        if (!contentRef.current || !numPages) return;
        const root = contentRef.current;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const idx = pageRefs.current.findIndex((el) => el === entry.target);
                        if (idx !== -1) setCurrentPage(idx + 1);
                    }
                });
            },
            { root, threshold: 0.5 }
        );
        pageRefs.current.forEach((el) => el && observer.observe(el));
        return () => observer.disconnect();
    }, [numPages, scale]);

    React.useEffect(() => {
        if (!contentRef.current) return;
        const el = contentRef.current;
        const ro = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry?.contentRect) setContentWidth(Math.floor(entry.contentRect.width));
        });
        ro.observe(el);
        setContentWidth(Math.floor(el.clientWidth));
        return () => ro.disconnect();
    }, []);

    return (
        <div className={`flex flex-col h-full ${className || ''}`}>
            {showControls && (
                <PDFViewerToolbar
                    leadingControls={leadingControls}
                    page={{
                        numPages,
                        currentPage,
                        canGoPrev,
                        canGoNext,
                        onPrevPage: () => goToPage(currentPage - 1),
                        onNextPage: () => goToPage(currentPage + 1)
                    }}
                    zoom={{
                        scale,
                        onZoomIn: increaseZoom,
                        onZoomOut: decreaseZoom,
                        onZoomReset: resetZoom
                    }}
                    actions={{
                        onPrint: handlePrint,
                        onDownload: handleDownload,
                        canUseActions: !!pdfBlob,
                        isPrinting,
                        isDownloading
                    }}
                />
            )}

            {/* Wrapper so the overlay thumb can sit outside the scrolling region. */}
            <div className="relative flex-grow min-h-0">
                <div
                    ref={setContentElement}
                    className="relative h-full overflow-auto scrollbar-hide bg-gray-100 rounded-lg"
                >
                    {errorMessage ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="flex flex-col items-center text-center p-8">
                                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                                    <svg
                                        className="w-8 h-8 text-red-500"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                        />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                                    Failed to load document
                                </h3>
                                <p className="text-gray-500 text-sm mb-4 max-w-md">
                                    {errorMessage}
                                </p>
                                <a
                                    href={pdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center gap-2 bg-gray-900 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors font-medium"
                                >
                                    Open in new tab
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                        />
                                    </svg>
                                </a>
                            </div>
                        </div>
                    ) : !file ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="flex flex-col items-center text-gray-600">
                                <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4">
                                    <ArrowPathIcon className="w-8 h-8 text-gray-400 animate-spin" />
                                </div>
                                <span className="text-sm text-gray-500 font-medium">
                                    Loading PDF...
                                </span>
                            </div>
                        </div>
                    ) : (
                        <Document
                            file={file}
                            onLoadSuccess={onDocumentLoadSuccess}
                            onLoadError={(error: unknown) =>
                                setErrorMessage(JSON.stringify(error) || 'Failed to load PDF')
                            }
                            loading={
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="flex flex-col items-center text-gray-600">
                                        <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4">
                                            <ArrowPathIcon className="w-8 h-8 text-gray-400 animate-spin" />
                                        </div>
                                        <span className="text-sm text-gray-500 font-medium">
                                            Rendering PDF...
                                        </span>
                                    </div>
                                </div>
                            }
                        >
                            {/* Mount pages only once ResizeObserver reports a real width:
                                width={0} leaves react-pdf on "Loading page…" forever, and numPages can arrive first. */}
                            {numPages && numPages > 0 && contentWidth > 0 && (
                                <div className="mx-auto py-6 flex flex-col items-center gap-6">
                                    {Array.from({ length: numPages }, (_, i) => (
                                        <div
                                            key={`wrap_${i + 1}`}
                                            ref={(el: HTMLDivElement | null) => {
                                                pageRefs.current[i] = el;
                                            }}
                                            className="flex justify-center w-full bg-white rounded-lg shadow-sm overflow-hidden"
                                        >
                                            <Page
                                                pageNumber={i + 1}
                                                width={Math.floor(contentWidth * 0.95 * scale)}
                                                onRenderSuccess={() =>
                                                    restoreScroll(i + 1 === numPages)
                                                }
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Document>
                    )}
                </div>

                <OverlayScrollThumb overlay={overlay} />
            </div>
        </div>
    );
};

export default PDFViewer;
